import React, { useMemo, useState } from "react";
import { Error, Modal } from "../../../components";
import { useEnterEvent } from "../../../context/EnterEvent";
import strings from "../../../utils/strings";

export const DivisionsModal = () => {
  const [open, setOpen] = useState(false);

  const {
    selectedEvent,
    setSelectedEvent,
    enrollData,
    setEnrollData,
    lateFee: eventLateFee,
    editId,
    isLateFeeAppliedOnEditEvent,
    isProUser,
  } = useEnterEvent();

  const max = useMemo(
    () => selectedEvent?.maxDivisionsEnterable,
    [selectedEvent?.maxDivisionsEnterable]
  );

  const selectedDivisions = useMemo(
    () => selectedEvent?.divisions?.filter(({ selected }) => selected) || [],
    [selectedEvent]
  );

  const isMaxReached = Boolean(max && max <= enrollData?.division?.length);


  const unavailableDivisions = useMemo(() => {
    const arr = [];
    enrollData?.division?.forEach(({ divisionTypeId }) => {
      const division = selectedEvent?.divisions?.find(
        ({ divisionTypeId: id }) => id === divisionTypeId
      );
      division?.divisionType?.unavailableDivisions?.forEach(
        ({ unavailableDivisionTypeId, divisionTypeId }) => {
          if (!arr.includes(divisionTypeId))
            arr.push(unavailableDivisionTypeId);
        }
      );
    });
    return arr;
  }, [enrollData, selectedEvent, open]);

  const getDivisionDiscountDetail = (divisionId) => {
    const eventDiscounts = Array.isArray(selectedEvent?.discounts)
      ? selectedEvent?.discounts
      : [selectedEvent?.discounts].filter(Boolean);

    const activeDivisionDiscount = eventDiscounts?.find(
      ({ discount_type, discountType, is_active, isActive }) => {
        const discountTypeValue = `${discount_type || discountType || ""}`.toLowerCase();
        const isActiveValue = is_active ?? isActive;
        return (
          discountTypeValue === "division" &&
          (isActiveValue === 1 || isActiveValue === "1" || isActiveValue === true)
        );
      }
    );

    return activeDivisionDiscount?.details?.find(
      ({ reference_id, referenceId }) =>
        `${reference_id ?? referenceId}` === `${divisionId}`
    );
  };

  const getDiscountedDivisionPrice = (basePrice, divisionId) => {
    const actualPrice = Number(basePrice || 0);
    const discountDetail = getDivisionDiscountDetail(divisionId);

    if (!discountDetail) {
      return actualPrice;
    }

    const discountMethod = `${discountDetail?.discount_method || discountDetail?.discountMethod || ""}`.toLowerCase();
    const discountValue = Number(discountDetail?.discount_value ?? discountDetail?.discountValue ?? 0);

    if (!discountValue || Number.isNaN(discountValue)) {
      return actualPrice;
    }

    if (discountMethod === "flat") {
      return Math.max(actualPrice - discountValue, 0);
    }

    if (discountMethod === "percentage" || discountMethod === "percent") {
      return Math.max(actualPrice - (actualPrice * discountValue) / 100, 0);
    }

    return actualPrice;
  };

  // Each pick is committed straight away so the compatibility rules and the
  // fee ladder are recalculated before the next division is chosen.
  const toggleDivisionSelect = (id) => {
    const newData = selectedEvent;
    const findIndex = newData.divisions?.findIndex(
      ({ id: divisionId }) => divisionId === id
    );

    if (findIndex < 0) return;
    newData.divisions[findIndex].selected =
      !newData.divisions[findIndex].selected;
    setSelectedEvent({ ...newData });

    const nonProDivisionIds = [];

    const allSelectedDivisions = newData.divisions
      ?.filter(({ selected }) => selected)
      .map(({ id, divisionTypeId, isProFee, isPro, proDivisionEntryFee }) => {
        let entryFee = 0;
        let lateFee = 0;

        const isProDivision = (isProFee || isPro) === "yes";
        const hasProDivisionEntryFee = Number(proDivisionEntryFee || 0) > 0;
        const hasEventProDivisionEntryFee = Number(selectedEvent?.proDivisionEntryFee || 0) > 0;
        const isFixedProDivisionFee =
          isProUser &&
          isProDivision &&
          (hasProDivisionEntryFee || hasEventProDivisionEntryFee);

        if (isFixedProDivisionFee) {
          // Division-level fixed pro fee does not use first/additional fee or late fee.
          entryFee = Number(
            hasProDivisionEntryFee
              ? proDivisionEntryFee
              : selectedEvent?.proDivisionEntryFee || 0
          );
          lateFee = 0;
        } else if (!nonProDivisionIds?.length) {
          // First non-pro division
          entryFee = selectedEvent?.divisionEntryFee;
          // If late fees were applied on the original event being edited, use raw values from selectedEvent
          // Otherwise, use the calculated eventLateFee (which considers current date)
          if (isLateFeeAppliedOnEditEvent && editId) {
            lateFee = selectedEvent?.firstDivisionSurcharge || 0;
          } else if (!editId) {
            lateFee = eventLateFee?.firstDivisionSurcharge || 0;
          } else {
            lateFee = 0;
          }
        } else {
          // Additional non-pro divisions
          entryFee = selectedEvent?.additionalDivisionEntryFee;
          // If late fees were applied on the original event being edited, use raw values from selectedEvent
          // Otherwise, use the calculated eventLateFee (which considers current date)
          if (isLateFeeAppliedOnEditEvent && editId) {
            lateFee = selectedEvent?.additionalDivisionSurcharge || 0;
          } else if (!editId) {
            lateFee = eventLateFee?.additionalDivisionSurcharge || 0;
          } else {
            lateFee = 0;
          }
        }

        const baseEntryFee = Number((lateFee || entryFee) || 0);
        const discountedCharge = getDiscountedDivisionPrice(baseEntryFee, id);
        const divisionDiscountAmount = Math.max(baseEntryFee - Number(discountedCharge || 0), 0);

        if (!isFixedProDivisionFee) {
          nonProDivisionIds.push(id);
        }

        const oldData = enrollData?.division?.find(
          ({ eventDivisionId }) => id === eventDivisionId
        );

        return {
          ...oldData,
          eventDivisionId: id,
          divisionTypeId,
          baseEntryFee,
          divisionDiscountAmount,
          entryFee,
          lateFee,
          finalEntryFee: Number(discountedCharge || 0),
        };
      });

    setEnrollData({ ...enrollData, division: allSelectedDivisions });
  };

  return (
    <>
      <div>
        <button
          type="submit"
          onClick={() => setOpen(true)}
          disabled={!selectedEvent || (max && max <= enrollData?.division?.length)}
          className="form-control text-center small-btn text-white bg-blue-gradiant border-0 p-0 "
        >
          <i className="fa-solid fa-circle-plus"></i> Add Division
        </button>{" "}
        {max && max <= enrollData?.division?.length ? (
          <strong className="text-red">{strings.event.division.max}</strong>
        ) : null}
      </div>
      <Modal
        style={{ minWidth: 900 }}
        show={open}
        close={setOpen}
        customClass="add-division-modal"
      >
        <div>
          <h2 className="text-current mt-3 mb-2 bold">
            Select Divisions
          </h2>
          <p className="font-xsss text-grey-600 mt-3 mb-4">
            The list refreshes automatically based on crossover rules.
          </p>

          {selectedDivisions?.length ? (
            <div className="selected-divisions mb-3">
              <strong className="font-xsss">SELECTED ({selectedDivisions.length}{max ? `/${max}` : ""})</strong>
              <div className="selected-divisions__list">
                {selectedDivisions.map((division) => (
                  <button
                    type="button"
                    key={`selected-division-${division?.id}`}
                    onClick={() => toggleDivisionSelect(division?.id)}
                    className="selected-division-chip"
                  >
                    {division?.name || division?.divisionDesc}
                    <i className="fa-solid fa-xmark"></i>
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {isMaxReached ? (
            <strong className="text-red d-block mb-3">{strings.event.division.max}</strong>
          ) : null}
          {selectedEvent?.divisions?.length ? (
            <div>
              <div className="row ">
                <div className="col col-lg-6">
                  <strong>DIVISION</strong>
                </div>
                <div className="hidden-sm col col-lg-6">
                  <strong>DIVISION RULE</strong>
                </div>
              </div>
              {selectedEvent?.divisions
                ?.sort((a, b) => {
                  const nameA = (a?.name || '').toLowerCase();
                  const nameB = (b?.name || '').toLowerCase();
                  return nameA.localeCompare(nameB);
                })
                ?.filter(({ isProFee, isPro }) => isProUser || (isProFee || isPro) !== "yes")
                ?.map((division, i) => (
                  division?.selected ? null : <Row
                    key={`division-modal-row-${i}`}
                    division={division}
                    unavailableDivisions={unavailableDivisions}
                    isMaxReached={isMaxReached}
                    toggleDivisionSelect={toggleDivisionSelect}
                  />
                ))}



              <div className="d-flex two-btns">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="form-control text-center small-btn text-white bg-blue-gradiant border-0 p-0 "
                >
                  CONFIRM <i className="fa-solid fa-circle-check ms-1" />
                </button>
              </div>
            </div>
          ) : (
            <div>
              <Error error="No Division Added" />
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

const Row = ({
  division,
  unavailableDivisions,
  isMaxReached,
  toggleDivisionSelect,
}) => {
  const { enrollData } = useEnterEvent();
  const selectedIds = enrollData?.division?.map(
    ({ divisionTypeId }) => divisionTypeId
  );

  let isAvailable = false;



  if (!selectedIds?.length || division?.pivot?.type != "yes") {
    isAvailable = true;
  } else if (!unavailableDivisions?.includes(division.divisionTypeId)) {

    const blockedBy = division?.divisionType?.unavailableDivisions?.findIndex(
      ({ unavailableDivisionTypeId }) =>
        selectedIds?.includes(unavailableDivisionTypeId)
    )

    if (blockedBy < 0 || blockedBy == undefined) {
      isAvailable = true;
    }
  }


  const isSelectable = isAvailable && !isMaxReached;

  return (
    <div
      key={`division-modal-${division?.id}`}
      className={`row custom-select-row division-select-row ${isSelectable ? "click-to-select" : "disabled-division"
        }`}
      onClick={() => isSelectable && toggleDivisionSelect(division?.id)}
    >
      <div key={`${division?.id}`} className={`col-lg-6`}>
        <i className="fa-regular fa-square mr-1"></i>
        {division?.name || division?.divisionDesc}
      </div>
      <div className="col col-lg-6 sm-no-right-padding">
        {division?.divisionName || division?.divisionDesc}
      </div>
    </div>
  );
};
